import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, X, ExternalLink } from "lucide-react";
import { Link } from "react-router";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AlertRecord {
  id: number;
  agencyId: number;
  internalId: string | null;
  lastName: string | null;
  firstName: string | null;
  reason: string | null;
  createdBy: number | null;
  createdAt: string;
  isRead: boolean;
  severity: string;
  alertType: string;
}

// ---------------------------------------------------------------------------
// Severity colors
// ---------------------------------------------------------------------------

const severityColors: Record<string, string> = {
  info: "bg-blue-100 text-blue-700 border-blue-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  critical: "bg-red-100 text-red-700 border-red-200",
};

const severityDot: Record<string, string> = {
  info: "bg-blue-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AlertsPanel() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: alertsData } = useQuery<{ data: AlertRecord[] }>({
    queryKey: ["alerts", "unread"],
    queryFn: () =>
      api.get<{ data: AlertRecord[] }>("/alerts", {
        params: { unreadOnly: "true" },
      }),
    refetchInterval: 60000, // refresh every 60 seconds
  });

  const markReadMutation = useMutation({
    mutationFn: (id: number) => api.put(`/alerts/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/alerts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const alerts = alertsData?.data ?? [];
  const unreadCount = alerts.filter((a) => !a.isRead).length;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell icon button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative rounded-md p-1.5 hover:bg-accent"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border bg-card shadow-lg sm:w-96">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-sm font-semibold">Alerts</h3>
            <Link
              to="/alerts"
              className="flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={() => setOpen(false)}
            >
              View all
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>

          {/* Alert list */}
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No new alerts.
              </div>
            ) : (
              alerts.slice(0, 10).map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    "flex items-start gap-3 border-b px-4 py-3 last:border-0",
                    !alert.isRead && "bg-accent/30",
                  )}
                >
                  {/* Severity dot */}
                  <div
                    className={cn(
                      "mt-1.5 h-2 w-2 flex-shrink-0 rounded-full",
                      severityDot[alert.severity] ?? severityDot.warning,
                    )}
                  />
                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {alert.firstName || alert.lastName ? (
                        <span className="text-sm font-medium">
                          {alert.firstName} {alert.lastName}
                        </span>
                      ) : null}
                      <span
                        className={cn(
                          "inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium",
                          severityColors[alert.severity] ??
                            severityColors.warning,
                        )}
                      >
                        {alert.severity}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                      {alert.reason}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {new Date(alert.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {/* Actions */}
                  <div className="flex flex-shrink-0 gap-1">
                    {!alert.isRead && (
                      <button
                        onClick={() => markReadMutation.mutate(alert.id)}
                        className="rounded p-1 hover:bg-accent"
                        title="Mark as read"
                      >
                        <Check className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                    <button
                      onClick={() => dismissMutation.mutate(alert.id)}
                      className="rounded p-1 hover:bg-accent"
                      title="Dismiss"
                    >
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
